import { t, type MessagePath } from "../i18n";

function idList(ids: number[]): string {
  return ids.join(",");
}

function typeLabel(type: string): string {
  if (type === "record") {
    return t("fields.record");
  }
  if (type === "calendar") {
    return t("fields.calendar");
  }
  if (type === "place") {
    return t("fields.place");
  }
  return type;
}

const Login = {
  get password_empty() {
    return t("login.passwordEmpty");
  },
  get password_incorrect() {
    return t("login.passwordIncorrect");
  },
  get email_invalid() {
    return t("login.emailInvalid");
  },
};

const Reset = {
  get password_empty() {
    return t("reset.passwordEmpty");
  },
  get unable_to_reset() {
    return t("reset.unableToReset");
  },
  get user_not_found() {
    return t("reset.userNotFound");
  },
  get link_expired() {
    return t("reset.linkExpired");
  },
  get passwords_do_not_match() {
    return t("reset.passwordsDoNotMatch");
  },
  get invalid_reset_link() {
    return t("reset.invalidResetLink");
  },
  get password_reset_successful() {
    return t("reset.passwordResetSuccessful");
  },
};

const Record = {
  createOneSuccess: (id: number) => t("record.createOneSuccess", { id }),
  createManySuccess: (num: number) => t("record.createManySuccess", { num }),
  get createFailed() {
    return t("record.createFailed");
  },
  updateFailed: (id: number) => t("record.updateFailed", { id }),
  updateMultiFailed: (ids: number[]) =>
    t("record.updateMultiFailed", { ids: idList(ids) }),
  updateSuccess: (id: number) => t("record.updateSuccess", { id }),
  updateMultiSuccess: (ids: number[]) =>
    t("record.updateMultiSuccess", { ids: idList(ids) }),
  deleteFailed: (id: number) => t("record.deleteFailed", { id }),
  deleteMultiFailed: (ids: number[]) =>
    t("record.deleteMultiFailed", { ids: idList(ids) }),
  deleteSuccess: (id: number) => t("record.deleteSuccess", { id }),
  deleteMultiSuccess: (ids: number[]) =>
    t("record.deleteMultiSuccess", { ids: idList(ids) }),
  get failedToLoadRecord() {
    return t("record.failedToLoad");
  },
  get failedToFindRecordId() {
    return t("record.failedToFindId");
  },
  get recordIdInvalid() {
    return t("record.idInvalid");
  },
};

const Calendar = {
  createSuccess: (name: string) => t("calendar.createSuccess", { name }),
  get createFailed() {
    return t("calendar.createFailed");
  },
  updateFailed: (name: string) => t("calendar.updateFailed", { name }),
  updateSuccess: (name: string) => t("calendar.updateSuccess", { name }),
  deleteFailed: (name: string) => t("calendar.deleteFailed", { name }),
  deleteSuccess: (name: string) => t("calendar.deleteSuccess", { name }),
  get failedToLoadRecord() {
    return t("calendar.failedToLoad");
  },
  calendarNameInvalid: (name: string) => t("calendar.nameInvalid", { name }),
  calendarNameExists: (name: string) => t("calendar.nameExists", { name }),
  get colorInvalid() {
    return t("calendar.colorInvalid");
  },
};

const Place = {
  createSuccess: (name: string) => t("place.createSuccess", { name }),
  get createFailed() {
    return t("place.createFailed");
  },
  updateFailed: (name: string) => t("place.updateFailed", { name }),
  updateSuccess: (name: string) => t("place.updateSuccess", { name }),
  deleteFailed: (name: string) => t("place.deleteFailed", { name }),
  deleteSuccess: (name: string) => t("place.deleteSuccess", { name }),
  get failedToLoadRecord() {
    return t("place.failedToLoad");
  },
  placeNameInvalid: (name: string) => t("place.nameInvalid", { name }),
  placeNameExists: (name: string) => t("place.nameExists", { name }),
  addressInvalid: () => t("place.addressEmpty"),
};

const Cron = {
  createSuccess: (name: string) => t("cron.createSuccess", { name }),
  get createFailed() {
    return t("cron.createFailed");
  },
  updateFailed: (id: string) => t("cron.updateFailed", { id }),
  updateSuccess: (id: string) => t("cron.updateSuccess", { id }),
  deleteFailed: (id: string) => t("cron.deleteFailed", { id }),
  deleteSuccess: (id: string) => t("cron.deleteSuccess", { id }),
  get failedToLoadRecord() {
    return t("cron.failedToLoad");
  },
  cronInvalid: () => t("cron.invalid"),
  cronJobExists: (name: string) => t("cron.exists", { name }),
  cronRunSuccess: (name: string) => t("cron.runSuccess", { name }),
  cronRunFailed: (name: string) => t("cron.runFailed", { name }),
};

const CalendarUi = {
  failedToFindEvent: (id: number) => t("calendarUi.failedToFindEvent", { id }),
  get failedToGetCalendarApi() {
    return t("calendarUi.failedToGetApi");
  },
};

const GridUi = {
  get emptySelection() {
    return t("grid.emptySelection");
  },
  get emptyData() {
    return t("grid.emptyData");
  },
  doesNotIncludeRecord: (id: number) =>
    t("grid.doesNotIncludeRecord", { id }),
  doesNotIncludeMultiRecord: (ids: number[]) =>
    t("grid.doesNotIncludeMultiRecord", { ids: idList(ids) }),
  failedToFindRowById: (id: number) => t("grid.failedToFindRowById", { id }),
  get pleaseSelectRecords() {
    return t("grid.pleaseSelectRecords");
  },
  failedToRestoreXRecord: (type: string) =>
    t("grid.failedToRestore", { type: typeLabel(type) }),
  failedToDeleteXRecord: (type: string) =>
    t("grid.failedToDelete", { type: typeLabel(type) }),
  successfullyRestoredXRecord: (count: number, type: string) =>
    t("grid.restored", { count, type: typeLabel(type) }),
  successfullyDeletedXRecord: (count: number, type: string) =>
    t("grid.deleted", { count, type: typeLabel(type) }),
  get copiedToClipboard() {
    return t("grid.copiedToClipboard");
  },
};

const Common = {
  get unableToLoadData() {
    return t("common.unableToLoadData");
  },
  pleaseEnterAValidX: (field: string) => {
    const fieldKeys: Record<string, MessagePath> = {
      "Start Date": "fields.startDate",
      "End Date": "fields.endDate",
      Name: "fields.name",
      Description: "fields.description",
    };
    const fieldKey = fieldKeys[field];
    return t("common.pleaseEnterValidField", {
      field: fieldKey ? t(fieldKey) : field,
    });
  },
};

const Settings = {
  get password_empty() {
    return t("settings.passwordEmpty");
  },
  get password_incorrect() {
    return t("settings.passwordIncorrect");
  },
  get confirmPasswordNotMatch() {
    return t("settings.confirmPasswordNotMatch");
  },
  get updateSuccess() {
    return t("settings.updateSuccess");
  },
  get userDetailNotFound() {
    return t("settings.userDetailNotFound");
  },
};

const User = {
  get failedToLoad() {
    return t("user.failedToLoad");
  },
  get usernameEmpty() {
    return t("user.usernameEmpty");
  },
  get passwordEmpty() {
    return t("user.passwordEmpty");
  },
  get passwordMismatch() {
    return t("user.passwordMismatch");
  },
  get createSuccess() {
    return t("user.createSuccess");
  },
  get createFailed() {
    return t("user.createFailed");
  },
  get updateSuccess() {
    return t("user.updateSuccess");
  },
  get updateFailed() {
    return t("user.updateFailed");
  },
  get deleteSuccess() {
    return t("user.deleteSuccess");
  },
  get deleteFailed() {
    return t("user.deleteFailed");
  },
  get cannotDeleteSelf() {
    return t("user.cannotDeleteSelf");
  },
  get selectOne() {
    return t("user.selectOne");
  },
};

export const SystemMsg = {
  Login,
  Record,
  Calendar,
  Place,
  CalendarUi,
  GridUi,
  Settings,
  User,
  Common,
  Cron,
  Reset,
};
